'use client'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useMutation } from '@apollo/client'
import {
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { CREATE_ONE_PRICELIST } from '../priceListQueries'

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
})

interface PriceListFormProps {
  data?: Record<string, unknown>
  onRequestClose?: () => void
}

export default function PriceListForm({
  data,
  onRequestClose,
}: PriceListFormProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
    },
  })

  const { control, handleSubmit } = form

  const [createPriceList, { loading }] = useMutation(CREATE_ONE_PRICELIST, {
    onCompleted: () => {
      toast.success('Price list created successfully')
      onRequestClose?.()
    },
    onError: (error) => {
      console.error('Error creating price list:', error)
      toast.error('Failed to create price list')
    },
    refetchQueries: ['GET_PRICELISTS'],
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    createPriceList({
      variables: {
        data: {
          name: values.name,
        },
      },
    })
  }

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <SheetTitle>Create Price List</SheetTitle>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Name"
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Price list name" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Creating...
                  </div>
                ) : (
                  'Create'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
